/**
 * O submenu de assuntos da Fazenda Boa Vista, dentro da tela de Residências.
 *
 * Papel: desenha o bloco "Fazenda Boa Vista" com um cartão para Manutenções e
 * um para Calendário, cada um levando à rota daquele módulo.
 *
 * Como usar: as rotas ficam escritas como texto (`/manutencoes`, `/calendario`),
 * e não como import de `modules/manutencoes` ou `modules/calendario` — a
 * fronteira entre módulos só se atravessa pelo `index.ts`, e nenhum dos dois
 * precisa ser alcançado por Residências para que o cartão leve até lá. Cada
 * cartão só é desenhado para quem alcança `ver` na própria chave
 * (`manutencoes-boa-vista`, `calendario-boa-vista`); com nenhuma das duas
 * concedida, ou enquanto o acesso ainda está sendo consultado, o componente
 * inteiro devolve `null`, e não um bloco com o título e nenhum cartão dentro.
 *
 * A grade fica em quatro colunas em qualquer largura (`colunasFixas`), com os
 * dois cartões nas duas primeiras, como o estilo em linha da origem; ela não
 * quebra como a grade da tela inicial.
 */
import { type ChaveModulo, acessoAlcanca } from '@concierge/shared';
import { type ComponentType, type SVGProps } from 'react';

import { useAcesso } from '@/shared/hooks';
import { CabecalhoDeSecao } from '@/shared/ui/cabecalho-de-secao';
import { CartaoDeModulo } from '@/shared/ui/cartao-de-modulo';
import { GradeDeCartoes } from '@/shared/ui/grade-de-cartoes';
import { IconeCalendario, IconeManutencoes } from '@/shared/ui/icones-de-modulo';

/** Um assunto do submenu: a chave que libera o cartão, a rota e o ícone. */
interface AssuntoBoaVista {
  readonly chave: ChaveModulo;
  readonly rota: string;
  readonly nome: string;
  readonly Icone: ComponentType<SVGProps<SVGSVGElement>>;
}

/**
 * Os dois assuntos, na ordem em que o bloco os desenha.
 *
 * A ordem é Manutenções antes de Calendário, e mexer nela reordena os dois
 * cartões na tela.
 */
const ASSUNTOS_BOA_VISTA: readonly AssuntoBoaVista[] = [
  {
    chave: 'manutencoes-boa-vista',
    rota: '/manutencoes',
    nome: 'Manutenções',
    Icone: IconeManutencoes,
  },
  {
    chave: 'calendario-boa-vista',
    rota: '/calendario',
    nome: 'Calendário',
    Icone: IconeCalendario,
  },
];

/**
 * O submenu de assuntos da Fazenda Boa Vista.
 *
 * @returns O bloco com o título, o subtítulo e os cartões liberados, ou `null`
 *   quando nenhum deles é liberado ou o acesso ainda está sendo consultado.
 */
export function MenuBoaVista() {
  const { data: acesso, isPending } = useAcesso();

  if (isPending || acesso === undefined) return null;

  const visiveis = ASSUNTOS_BOA_VISTA.filter((assunto) =>
    acessoAlcanca(acesso, assunto.chave, 'ver'),
  );

  if (visiveis.length === 0) return null;

  return (
    <div className="mb-9">
      <CabecalhoDeSecao
        titulo="Fazenda Boa Vista"
        subtitulo="PORTO FELIZ, SP • ESCOLHA UM ASSUNTO"
      />

      <GradeDeCartoes colunasFixas>
        {visiveis.map((assunto) => (
          <li key={assunto.chave}>
            <CartaoDeModulo rota={assunto.rota} nome={assunto.nome} Icone={assunto.Icone} />
          </li>
        ))}
      </GradeDeCartoes>
    </div>
  );
}
